import React, { useLayoutEffect, useMemo, useState } from 'react';
import PropTypes, { InferProps } from 'prop-types';

import classnames from 'classnames/bind';

import useDocumentGenerationSteps from '@HOOKS/store/useDocumentGenerationSteps';

import { OptionsStep, SelectContentStep, SelectContentStepData } from '@REDUCERS';
import { ContentChapter } from '@INTERFACES/api/content';

import GenerationSteps from '@CONSTANTS/GENERATION_STEPS.constant';

import { GenerationMode } from '@PAGES/manager/options/Options.page';
import getDataByTags from '@PAGES/manager/select-content/helpers/getDataByTags';
import filterDataByTags from '@PAGES/manager/select-content/helpers/filterDataByTags';
import filterDataBySearch from '@PAGES/manager/select-content/helpers/filterDataBySearch';
import CompactContentTreeView from '@PAGES/manager/select-content/components/CompactContentTreeView/CompactContentTreeView.component';

import styles from './Content.module.scss';

const cx: CX = classnames.bind(styles);

// Error Boundary Component
class ContentErrorBoundary extends React.Component<
{ children: React.ReactNode },
{ hasError: boolean; error?: Error }
> {
    constructor(props: { children: React.ReactNode }) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(error: Error) {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        console.error('Content component error:', error, errorInfo);
    }

    render() {
        const { hasError } = this.state;
        const { children } = this.props;

        if (hasError) {
            return (
                <div className={cx('error-boundary')}>
                    <div className={cx('error-message')}>
                        <h3>Something went wrong</h3>
                        <p>There was an error loading the content. Please try refreshing the page.</p>
                        <button
                            type="button"
                            onClick={() => this.setState({ hasError: false })}
                            className={cx('retry-button')}
                        >
                            Try Again
                        </button>
                    </div>
                </div>
            );
        }

        return children;
    }
}

function Content(props: Props) {
    const {
        chaptersData,
        selectedTagsIds,
        searchValue,
        setSearchValue,
    } = props;

    const {
        actions: documentGenerationStepsActions,
        services: documentGenerationStepsServices,
    } = useDocumentGenerationSteps();

    const optionsStep: OptionsStep = documentGenerationStepsServices.getStepData(GenerationSteps.OPTIONS);
    const selectContentStep: SelectContentStep = documentGenerationStepsServices.getStepData(
        GenerationSteps.SELECT_CONTENT,
    );

    // Initialize state variables first (before any early returns)
    const isPartial = optionsStep?.data?.id === GenerationMode.PARTIAL;

    // Initialize with empty sets - no mandatory logic
    const initialChaptersState = selectContentStep?.data?.chaptersIds || [];
    const initialSectionsState = selectContentStep?.data?.sectionsIds || [];
    const initialVariantsState = selectContentStep?.data?.variantsIds || [];

    const [selectedChaptersIds, setSelectedChaptersIds] = useState<Set<number>>(
        new Set(initialChaptersState),
    );
    const [selectedSectionsIds, setSelectedSectionsIds] = useState<Set<number>>(
        new Set(initialSectionsState),
    );
    const [selectedVariantsIds, setSelectedVariantsIds] = useState<Set<number>>(
        new Set(initialVariantsState),
    );

    useLayoutEffect(() => {
        const { chaptersIds, sectionsIds, variantsIds } = getDataByTags({
            chaptersData,
            initialChaptersIds: selectedChaptersIds,
            initialSectionsIds: selectedSectionsIds,
            initialVariantsIds: selectedVariantsIds,
            selectedTagsIds,
            isPartial,
        });

        setSelectedChaptersIds(chaptersIds);
        setSelectedSectionsIds(sectionsIds);
        setSelectedVariantsIds(variantsIds);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chaptersData, selectedTagsIds, isPartial]);

    // Removed mandatory sections logic

    useLayoutEffect(() => {
        const data: SelectContentStepData = {
            chaptersIds: Array.from(selectedChaptersIds),
            sectionsIds: Array.from(selectedSectionsIds),
            variantsIds: Array.from(selectedVariantsIds),
            tagsIds: Array.from(selectedTagsIds),
        };

        // Step is complete if any content is selected
        const isComplete = data.variantsIds.length > 0
            || data.sectionsIds.length > 0
            || data.chaptersIds.length > 0;

        documentGenerationStepsActions.apply({
            id: GenerationSteps.SELECT_CONTENT,
            isComplete,
            data,
        });
    }, [
        documentGenerationStepsActions,
        selectedChaptersIds,
        selectedSectionsIds,
        selectedVariantsIds,
        selectedTagsIds,
        chaptersData,
    ]);

    // Remove hasSelectedChapters check - always show tree view

    // FILTER BY TAGS
    const filteredByTagsData = useMemo(
        () => filterDataByTags({
            chaptersData,
            selectedTagsIds,
            isPartial,
        }),
        [chaptersData, selectedTagsIds, isPartial],
    );

    // FILTER BY SEARCH
    const filteredBySearchData = useMemo(
        () => filterDataBySearch({
            chaptersData: filteredByTagsData,
            searchValue,
        }),
        [filteredByTagsData, searchValue],
    );

    // Guard against undefined steps data (after all hooks)
    if (!optionsStep?.data) {
        return <div>Loading...</div>;
    }

    return (
        <ContentErrorBoundary>
            <div className={cx('content')}>
                {filteredBySearchData.length > 0 ? (
                    <CompactContentTreeView
                        chaptersData={filteredBySearchData}
                        selectedChaptersIds={selectedChaptersIds}
                        selectedSectionsIds={selectedSectionsIds}
                        selectedVariantsIds={selectedVariantsIds}
                        setSelectedChaptersIds={setSelectedChaptersIds}
                        setSelectedSectionsIds={setSelectedSectionsIds}
                        setSelectedVariantsIds={setSelectedVariantsIds}
                        isPartial={isPartial}
                    />
                ) : (
                    <div className={cx('chapters-not-found')}>
                        No content found matching your search and filter criteria.
                    </div>
                )}
            </div>
        </ContentErrorBoundary>
    );
}

Content.propTypes = {
    selectedTagsIds: PropTypes.instanceOf(Set<number>).isRequired,
    searchValue: PropTypes.string.isRequired,
    setSearchValue: PropTypes.func.isRequired,
};

type Props = InferProps<typeof Content.propTypes> & {
    chaptersData: ContentChapter[];
};

export default Content;
